import { useState } from "react";
import hookIconUrl from "../../images/chrome-extension/hook-icon.png";

function IconHook() {
  return (
    <span
      className="debug-bar__hook-icon"
      style={{
        WebkitMaskImage: `url(${hookIconUrl})`,
        maskImage: `url(${hookIconUrl})`,
      }}
      aria-hidden="true"
    />
  );
}

function IconRefresh() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.5 8a5.5 5.5 0 0 1 9.3-4.03M13.5 8A5.5 5.5 0 0 1 4.2 12.03"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M11.5 2.5V5h2.5M4.5 13.5V11H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function shortenId(id) {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

// The bar mirrors one access state at a time: a paid entitlement, a running
// trial, free minutes being spent, or those minutes exhausted.
function resolveAccessState({ isPro, trialMode, usageHit, usageProgress }) {
  if (isPro) return "upgraded";
  if (trialMode) return "trialing";
  if (usageHit || usageProgress >= 0.99) return "capped";
  return "using";
}

const ACCESS_STATES = [
  {
    key: "trialing",
    label: "Trialing",
    title: "Start a fresh trial",
    handler: "onStartTrialModeClick",
  },
  {
    key: "using",
    label: "Using",
    title: "End any trial and reset the free minutes",
    handler: "onUsingUsageClick",
  },
  {
    key: "capped",
    label: "Capped",
    title: "End any trial and exhaust the free minutes",
    handler: "onForceMaxUsageClick",
  },
  {
    // Pro comes from a real Stripe entitlement, so this segment cannot be
    // faked locally — clicking it opens the same checkout the popup does.
    key: "upgraded",
    label: "Upgraded",
    title: "Open checkout to buy a real entitlement",
    handler: "onUpgradeClick",
  },
];

// The nudges are gated behind conditions that take days to reach for real.
// These stand them up on demand; nothing is tracked and no counter moves.
const NUDGES = [
  {
    key: "promo",
    label: "Promo",
    title:
      "Show the cap offer in the Unlock PRO rotation — needs Using or Capped",
  },
  {
    key: "rate",
    label: "Rate",
    title: "Show the rating banner",
  },
  {
    key: "update",
    label: "Update",
    title: "Show the product updates banner",
  },
];

// The in-page notice shown when free minutes run out. Weekly and Monthly show
// it on the current YouTube tab with that window's wording (the discount
// variant while Promo is on); Reset forgets the once-per-period record so the
// real one can appear again.
const CAP_NOTICE_ACTIONS = [
  {
    key: "weekly",
    label: "Weekly",
    title:
      "Show the cap notice on this tab, weekly wording — with Promo on, the discount variant",
  },
  {
    key: "monthly",
    label: "Monthly",
    title:
      "Show the cap notice on this tab, monthly wording — with Promo on, the discount variant",
  },
  {
    key: "reset",
    label: "Reset",
    title: "Forget that the cap notice was shown or dismissed this period",
  },
];

export default function DebugBar({
  testMode,
  paymentHookId,
  hookBusy,
  hookError,
  isPro,
  trialMode,
  usageHit,
  usageProgress,
  nudgePreview,
  promoPreviewNote,
  capNoticeStatus,
  onTestModeChange,
  onRefreshPaymentHookClick,
  onStartTrialModeClick,
  onUsingUsageClick,
  onForceMaxUsageClick,
  onUpgradeClick,
  onNudgePreviewChange,
  onCapNoticePreview,
  onCapNoticeReset,
}) {
  const [copied, setCopied] = useState(false);
  const developmentControlsEnabled =
    process.env.NODE_ENV === "development";
  const accessState = resolveAccessState({
    isPro,
    trialMode,
    usageHit,
    usageProgress,
  });
  const accessHandlers = {
    onStartTrialModeClick,
    onUsingUsageClick,
    onForceMaxUsageClick,
    onUpgradeClick,
  };

  function onCopy() {
    if (!paymentHookId) return;
    navigator.clipboard.writeText(paymentHookId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="debug-bar">
      {developmentControlsEnabled && (
        <div className="debug-bar__group">
          <div
            className={`debug-bar__segmented${hookBusy ? " debug-bar__segmented--busy" : ""}`}
            role="group"
            aria-label="Payment mode"
          >
            <button
              type="button"
              className={`debug-bar__segment${!testMode ? " debug-bar__segment--active" : ""}`}
              disabled={hookBusy}
              aria-pressed={!testMode}
              onClick={() => !testMode || onTestModeChange(false)}
            >
              Live
            </button>
            <button
              type="button"
              className={`debug-bar__segment${testMode ? " debug-bar__segment--active" : ""}`}
              disabled={hookBusy}
              aria-pressed={testMode}
              onClick={() => testMode || onTestModeChange(true)}
            >
              Test
            </button>
          </div>

          <span className="debug-bar__sep" aria-hidden="true" />

          <div
            className={`debug-bar__segmented${hookBusy ? " debug-bar__segmented--busy" : ""}`}
            role="group"
            aria-label="Access state"
          >
            {ACCESS_STATES.map(({ key, label, title, handler }) => (
              <button
                key={key}
                type="button"
                className={`debug-bar__segment${accessState === key ? " debug-bar__segment--active" : ""}`}
                disabled={hookBusy}
                aria-pressed={accessState === key}
                title={title}
                onClick={() => accessHandlers[handler]?.()}
              >
                {label}
              </button>
            ))}
          </div>

          <span className="debug-bar__sep" aria-hidden="true" />

          <div
            className="debug-bar__segmented"
            role="group"
            aria-label="Nudge preview"
          >
            {NUDGES.map(({ key, label, title }) => {
              const on = nudgePreview?.[key] === true;
              return (
                <button
                  key={key}
                  type="button"
                  className={`debug-bar__segment${on ? " debug-bar__segment--active" : ""}`}
                  aria-pressed={on}
                  title={title}
                  onClick={() => onNudgePreviewChange?.(key, !on)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <span className="debug-bar__sep" aria-hidden="true" />

          <div
            className="debug-bar__segmented"
            role="group"
            aria-label="Cap notice preview"
          >
            {CAP_NOTICE_ACTIONS.map(({ key, label, title }) => (
              <button
                key={key}
                type="button"
                className="debug-bar__segment"
                title={title}
                onClick={() =>
                  key === "reset"
                    ? onCapNoticeReset?.()
                    : onCapNoticePreview?.(key)
                }
              >
                {label}
              </button>
            ))}
          </div>

          {(promoPreviewNote || capNoticeStatus) && (
            <span className="debug-bar__note" role="status">
              {[promoPreviewNote, capNoticeStatus].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
      )}

      <div className="debug-bar__group debug-bar__group--grow">
        {hookError ? (
          <span className="debug-bar__error" title={hookError} role="alert">
            {hookError}
          </span>
        ) : (
          <div
            className={`debug-bar__chip${hookBusy ? " debug-bar__chip--busy" : ""}`}
            aria-busy={hookBusy || undefined}
          >
            <IconHook />
            <button
              type="button"
              className={`debug-bar__chip-value${copied ? " debug-bar__chip-value--copied" : ""}`}
              title={
                copied
                  ? "Copied"
                  : paymentHookId
                    ? "Copy payment hook id"
                    : undefined
              }
              aria-label={copied ? "Copied" : "Copy payment hook id"}
              disabled={!paymentHookId || hookBusy}
              onClick={onCopy}
            >
              {shortenId(paymentHookId)}
            </button>
            <div className="debug-bar__actions" role="group" aria-label="Payment hook">
              <button
                type="button"
                className="debug-bar__action"
                title="Create new payment hook"
                aria-label="Create new payment hook"
                disabled={hookBusy}
                onClick={onRefreshPaymentHookClick}
              >
                <IconRefresh />
              </button>
            </div>
          </div>
        )}
      </div>

      {developmentControlsEnabled && (
        <span className="debug-bar__hash" title={process.env.COMMIT_HASH}>
          {process.env.COMMIT_HASH}
        </span>
      )}
    </div>
  );
}
